import styled from 'styled-components';
import dayjs from 'dayjs';
import { NextRouter, useRouter } from 'next/router';
import { Text } from 'components/primitives/typography';
import { useOnSuccessActionModal } from 'components/inbox/hooks/useOnSuccessActionModal';
import useModal from 'components/remodal/v2/useModal';
import useBreakpoints, { SMALLSCREEN_QUERY } from 'hooks/useBreakpoints';
import { Boat, Booking } from 'swagger/models';
import EditInquiryModal from 'components/modals/EditInquiryModal';
import { useState } from 'react';
import CancelPreApprovalModal from 'components/modals/CancelPreApprovalModal';
import PreApproveModal from 'components/modals/PreApproveModal';
import MultiBookModal from 'components/modals/MultiBookModal';
import useEditBookingModal from 'components/inbox/bookingPanel/useEditBookingModal';
import useLazySnackbar, { EnqueueSnackbarLazyType } from 'components/banners/useLazySnackbar';
import Button from 'components/ui/Button/Button';
import snakeCase from 'lodash/snakeCase';
import useRudderStackTrack from 'hooks/useRudderStackTrack';
import OwnerBookingActionsModal from '../../../../../pages/a/bookings/[bookingId]/owner_action/[action]';
import { useBreakpoint } from '../../BreakpointProvider';
import { valueEmpty } from '../../../helpers';
import { FlexCenterBoth, FlexCol } from '../../primitives/flex';
import { canPreApproveAndGrandfathered, canPreApproveIfReadyForRental } from '../BookingBooleansHelpers';

export const approveAction = (
  booking,
  router: NextRouter,
  enqueueSnackbar: EnqueueSnackbarLazyType,
  handleApproveAction: () => void,
  openMultibookModal: () => void
) => {
  if (booking.state === 'payment_held') {
    if (booking.can_approve) {
      if (booking.multibook_enabled_missing && booking.offers_half_day_packages) {
        return {
          available: true,
          onClick: openMultibookModal,
        };
      }
      if (booking.agreed_to_uscg_regulations_at_missing && booking.instant_book_enabled) {
        return {
          available: true,
          onClick: () => {
            window.location.href = `/bookings/${booking.id}/agree_uscg_regulations`;
          },
        };
      }
      if (booking.could_approve_if_captain_ready_for_rental) {
        return {
          available: true,
          onClick: () => {
            window.location.href = `/bookings/${booking.id}/rental_captain_settings`;
          },
        };
      }
      return {
        available: true,
        onClick: handleApproveAction,
      };
    }
    if (booking.could_approve_if_ready_for_rental && booking.could_approve_if_captain_ready_for_rental) {
      return {
        available: true,
        onClick: () => {
          window.location.href = `/bookings/${booking.id}/rental_captain_settings`;
        },
      };
    }
    let reason;
    if (booking.conflicting_dates) {
      reason = '';
    } else if (booking.waiting_on_renter_verification) {
      reason = 'The renter has not yet completed his or her renter verification.';
    } else if (booking.boat.state === 'pending_insurance') {
      reason = 'This booking is unable to be approved at this time.';
    } else {
      reason = 'This booking is unable to be approved at this time. Please contact contact@boatsetter.com for help';
    }
    return {
      actionText: reason,
      available: true,
      onClick: () => {
        if (booking.conflicting_dates) {
          enqueueSnackbar(
            !valueEmpty(reason)
              ? reason
              : 'This booking is unable to be approved. This boat is unavailable on these dates.',
            { variant: 'error', autoHideDuration: 4000 }
          );
        }
      },
      textOnly: !valueEmpty(reason),
    };
  }
  return {
    actionText: '',
    available: false,
    onClick: () => null,
  };
};

export const useEditInquiryModal = (booking: Booking) => {
  const [smallScreen] = useBreakpoints(SMALLSCREEN_QUERY);
  const { modal, openModal } = useModal(<EditInquiryModal booking={booking} />, {
    isOnRight: true,
    title: smallScreen && 'Edit booking',
    trackingName: 'edit_inquiry_modal',
  });
  const isAvailable = booking.state === 'initialized' || booking.state === 'pre_approved';

  return { isAvailable, modal, openModal };
};

export const preApproveAction = (booking, router, openPreApproveModal) => {
  if (canPreApproveAndGrandfathered(booking)) {
    if (booking.agreed_to_uscg_regulations_at_missing && booking.instant_book_enabled) {
      return {
        available: true,
        onClick: () => {
          window.location.href = `/bookings/${booking.id}/agree_uscg_regulations`;
        },
      };
    }
    if (booking.could_approve_if_captain_ready_for_rental) {
      return {
        available: true,
        onClick: () => {
          window.location.href = `/bookings/${booking.id}/rental_captain_settings`;
        },
      };
    }
    return {
      available: true,
      onClick: openPreApproveModal,
    };
  }
  if (canPreApproveIfReadyForRental(booking) && booking.could_approve_if_captain_ready_for_rental) {
    return {
      available: true,
      onClick: () => {
        window.location.href = `/bookings/${booking.id}/rental_captain_settings`;
      },
    };
  }
  return {
    available: false,
    onClick: () => null,
  };
};

const SidebarOwnerActions = ({ booking, boat }: { booking: Booking; boat: Boat }) => {
  const { enqueueSnackbar } = useLazySnackbar();
  const breakpoints = useBreakpoint();
  const smallScreen = breakpoints.xs || breakpoints.sm;
  const router = useRouter();
  const onSuccess = useOnSuccessActionModal();
  const rudderTrack = useRudderStackTrack();

  const tripDuration = dayjs(booking.trip_finish).diff(dayjs(booking.trip_start), 'hours');

  const { modal: editBookingModal, openModal: openEditBookingModal } = useEditBookingModal(booking, () =>
    onSuccess(false)
  );

  const [ownerBookingActionType, setOwnerBookingActionType] = useState<'approve' | 'decline'>('approve');
  const { modal: ownerBookingActionsModal, openModal: openOwnerBookingActionsModal } = useModal(
    <OwnerBookingActionsModal
      openEditBookingModal={openEditBookingModal}
      booking={booking}
      actionType={ownerBookingActionType}
      onSuccess={onSuccess}
    />,
    {
      size: ownerBookingActionType === 'decline' ? 'medium' : 'large',
      overridePadding: 'small',
      trackingName: 'owner_booking_actions_modal',
    }
  );
  const handleOpenOwnerBookingActionsModal = (actionType: 'approve' | 'decline') => {
    setOwnerBookingActionType(actionType);
    openOwnerBookingActionsModal();
  };

  const { modal: cancelPreApprovalModal, openModal: openCancelPreApprovalModal } = useModal(
    <CancelPreApprovalModal booking={booking} onSuccess={onSuccess} />,
    {
      size: 'small',
      trackingName: 'cancel_pre_approval_modal',
    }
  );
  const { modal: preApproveModal, openModal: openPreApproveModal } = useModal(
    <PreApproveModal booking={booking} onSuccess={onSuccess} />,
    {
      size: 'medium',
      isOnRight: true,
      title: smallScreen && 'Pre-approve inquiry',
      trackingName: 'pre_approve_modal',
    }
  );
  const { modal: editInquiryModal, openModal: openEditInquiryModal, isAvailable } = useEditInquiryModal(booking);

  const { modal: multibookModal, openModal: openMultibookModal } = useModal(
    <MultiBookModal booking={booking} actionType="approve" />,
    {
      size: 'small',
      trackingName: 'multibook_modal',
    }
  );

  const approve = approveAction(
    booking,
    router,
    enqueueSnackbar,
    () => handleOpenOwnerBookingActionsModal('approve'),
    openMultibookModal
  );

  const onClickApprove = () => {
    approve.onClick();
    rudderTrack('booking_action_owner', {
      action: 'approve',
      location: 'owner_side_bar_action',
      booking: booking.id,
    });
  };

  const buttons = [
    {
      actionText: approve.actionText || 'Approve',
      available: approve.available,
      onClick: onClickApprove,
      textOnly: approve.textOnly,
    },
    {
      actionText: 'Pre-approve',
      available: preApproveAction(booking, router, openPreApproveModal).available && booking.package.available,
      onClick: () => {
        preApproveAction(booking, router, openPreApproveModal).onClick();
        rudderTrack('booking_action_owner', {
          action: 'pre-approve',
          location: 'owner_side_bar_action',
          booking: booking.id,
        });
      },
    },
    {
      actionText: 'The boat is unavailable on these dates or the renter is not eligible to make this booking.',
      available: !booking.package.available,
      textOnly: true,
      onClick: () => {},
    },
    {
      actionText: 'Edit inquiry',
      available: tripDuration <= 8 && isAvailable, // true
      variant: booking.user_type,
      outline: true,
      onClick: () => {
        openEditInquiryModal();
      },
    },
    {
      actionText: 'Decline',
      available: booking.can_decline && booking.state === 'payment_held',
      onClick: () => {
        if (booking.can_decline) {
          handleOpenOwnerBookingActionsModal('decline');
        } else {
          window.location.href = `/bookings/${booking.id}/edit`;
        }
      },
      outline: true,
    },

    {
      actionText: 'Leave review',
      available: ['concluded', 'ashore', 'disputed'].includes(booking.state) && booking.show_owner_leave_a_review,
      onClick: () => router.push(`/a/bookings/${booking.id}/review`),
    },
    {
      actionText: 'View booking details',
      available:
        booking.state !== 'expired' &&
        (booking.can_view_details || (booking.state !== 'pre_approved' && booking.state !== 'initialized')),
      onClick: () => {
        window.location.href = booking.details_url;
      },
    },
    {
      actionText: 'Cancel pre-approval',
      available: booking.can_edit_or_cancel && booking.state === 'pre_approved',
      onClick: openCancelPreApprovalModal,
      justText: true,
    },
  ].filter((b) => !!b.available);

  const renderButton = (button, index) => {
    if (button.justText) {
      return <OwnerText onClick={button.onClick}>{button.actionText}</OwnerText>;
    }

    if (buttons.length > 2 && index === 2) {
      return (
        <Button
          variant="owner"
          type="button"
          justText
          onClick={() => {
            rudderTrack('booking_action_sidebar', {
              bookingId: booking.id,
              bookingState: booking.state,
              action: snakeCase(button.actionText),
              location: 'owner_side_bar_action',
            });
            button.onClick();
          }}
          text={
            <Text fWeight="500" as="span">
              {button.actionText}
            </Text>
          }
        />
      );
    }
    return button.textOnly ? (
      <TextOnlyAction>{button.actionText}</TextOnlyAction>
    ) : (
      <Button
        variant={button.variant || (index === 0 && booking.user_type === 'owner' ? 'yellow' : booking.user_type)}
        text={button.actionText}
        onClick={button.onClick}
        justText={button.textOnly}
        outline={button.outline || index !== 0}
      />
    );
  };

  return (
    <FlexWrapper gap="12px">
      {['pending_insurance', 'insurance_denied'].includes(boat.state) &&
        ['initialized', 'pre_approved'].includes(booking.state) && (
          <TextOnlyAction>This boat can no longer accept bookings.</TextOnlyAction>
        )}

      {buttons.map((button, index) => (
        <FlexCenterBoth key={button.actionText}>{renderButton(button, index)}</FlexCenterBoth>
      ))}

      {editInquiryModal}
      {ownerBookingActionsModal}
      {cancelPreApprovalModal}
      {preApproveModal}
      {multibookModal}
      {editBookingModal}
    </FlexWrapper>
  );
};

const FlexWrapper = styled(FlexCol)`
  button {
    width: 100%;
  }
`;

const OwnerText = styled(FlexCenterBoth)`
  color: #0e7873;
  font-size: 14px;
  text-align: center;
  font-weight: 500;
  cursor: pointer;
`;

const TextOnlyAction = styled.p`
  color: #17233c;
  font-weight: 500;
  font-size: 14px;
  margin: 0;
`;

export default SidebarOwnerActions;
